/**
 * Cliente React Query para el panel "Ajustes → Notificaciones" (v1.5.0-rc43).
 *
 * Wraps los endpoints CRUD de `/api/v1/notifications/settings/*` agregados
 * en backend rc41 (Fase 1).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";

// ────────────────────────────────────────────────────────────────────
// Types — espejo de schemas Pydantic en backend
// ────────────────────────────────────────────────────────────────────
export type Canal = "push" | "in_app" | "email";

export type NotificationTopic = {
  codigo: string;
  categoria: string;
  nombre: string;
  descripcion: string | null;
  activo: boolean;
  created_at: string;
};

export type NotificationSubscription = {
  id: string;
  user_id: string;
  topic: string;
  canal: Canal;
  activo: boolean;
  created_at: string;
  updated_at: string;
};

export type NotificationGroup = {
  id: string;
  nombre: string;
  descripcion: string | null;
  activo: boolean;
  created_at: string;
  updated_at: string;
};

export type NotificationGroupTopic = {
  id: string;
  group_id: string;
  topic: string;
  canal: Canal;
  created_at: string;
};

export type NotificationGroupMember = {
  id: string;
  group_id: string;
  user_id: string;
  created_at: string;
};

export type NotificationGroupDetail = NotificationGroup & {
  topics: NotificationGroupTopic[];
  members: NotificationGroupMember[];
};

// ────────────────────────────────────────────────────────────────────
// Query keys
// ────────────────────────────────────────────────────────────────────
const KEY = ["notif-settings"] as const;

// ────────────────────────────────────────────────────────────────────
// Topics
// ────────────────────────────────────────────────────────────────────
export function useTopics() {
  return useQuery({
    queryKey: [...KEY, "topics"],
    queryFn: () =>
      apiCall<NotificationTopic[]>("get", "/api/v1/notifications/settings/topics"),
  });
}

// ────────────────────────────────────────────────────────────────────
// Subscriptions
// ────────────────────────────────────────────────────────────────────
export function useSubscriptions(filters?: { user_id?: string; topic?: string }) {
  return useQuery({
    queryKey: [...KEY, "subscriptions", filters ?? {}],
    queryFn: () => {
      const searchParams: Record<string, string> = {};
      if (filters?.user_id) searchParams.user_id = filters.user_id;
      if (filters?.topic) searchParams.topic = filters.topic;
      return apiCall<NotificationSubscription[]>(
        "get",
        "/api/v1/notifications/settings/subscriptions",
        Object.keys(searchParams).length ? { searchParams } : undefined,
      );
    },
  });
}

export function useCreateSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { user_id: string; topic: string; canal?: Canal; activo?: boolean }) =>
      apiCall<NotificationSubscription>(
        "post",
        "/api/v1/notifications/settings/subscriptions",
        { json: { canal: "push", activo: true, ...input } },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, "subscriptions"] }),
  });
}

export function useUpdateSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, activo }: { id: string; activo: boolean }) =>
      apiCall<NotificationSubscription>(
        "patch",
        `/api/v1/notifications/settings/subscriptions/${id}`,
        { json: { activo } },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, "subscriptions"] }),
  });
}

export function useDeleteSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiCall<{ detail: string }>(
        "delete",
        `/api/v1/notifications/settings/subscriptions/${id}`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, "subscriptions"] }),
  });
}

export function useBulkUpsertSubscriptions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      items: { user_id: string; topic: string; canal?: Canal }[];
      activo: boolean;
    }) =>
      apiCall<{ detail: string }>(
        "post",
        "/api/v1/notifications/settings/subscriptions/bulk",
        { json: input },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, "subscriptions"] }),
  });
}

// ────────────────────────────────────────────────────────────────────
// Groups
// ────────────────────────────────────────────────────────────────────
export function useGroups() {
  return useQuery({
    queryKey: [...KEY, "groups"],
    queryFn: () =>
      apiCall<NotificationGroup[]>("get", "/api/v1/notifications/settings/groups"),
  });
}

export function useGroupDetail(groupId: string | null) {
  return useQuery({
    queryKey: [...KEY, "group", groupId],
    queryFn: () =>
      apiCall<NotificationGroupDetail>(
        "get",
        `/api/v1/notifications/settings/groups/${groupId}`,
      ),
    enabled: !!groupId,
  });
}

export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { nombre: string; descripcion?: string }) =>
      apiCall<NotificationGroup>(
        "post",
        "/api/v1/notifications/settings/groups",
        { json: input },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, "groups"] }),
  });
}

export function useUpdateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { nombre?: string; descripcion?: string | null; activo?: boolean };
    }) =>
      apiCall<NotificationGroup>(
        "patch",
        `/api/v1/notifications/settings/groups/${id}`,
        { json: data },
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: [...KEY, "groups"] });
      qc.invalidateQueries({ queryKey: [...KEY, "group", vars.id] });
    },
  });
}

export function useDeleteGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiCall<{ detail: string }>(
        "delete",
        `/api/v1/notifications/settings/groups/${id}`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, "groups"] }),
  });
}

export function useAddGroupTopic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      groupId,
      topic,
      canal = "push",
    }: {
      groupId: string;
      topic: string;
      canal?: Canal;
    }) =>
      apiCall<NotificationGroupTopic>(
        "post",
        `/api/v1/notifications/settings/groups/${groupId}/topics`,
        { json: { topic, canal } },
      ),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: [...KEY, "group", vars.groupId] }),
  });
}

export function useRemoveGroupTopic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      groupId,
      topic,
      canal,
    }: {
      groupId: string;
      topic: string;
      canal: Canal;
    }) =>
      apiCall<{ detail: string }>(
        "delete",
        `/api/v1/notifications/settings/groups/${groupId}/topics/${topic}/${canal}`,
      ),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: [...KEY, "group", vars.groupId] }),
  });
}

export function useAddGroupMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      apiCall<NotificationGroupMember>(
        "post",
        `/api/v1/notifications/settings/groups/${groupId}/members`,
        { json: { user_id: userId } },
      ),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: [...KEY, "group", vars.groupId] }),
  });
}

export function useRemoveGroupMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      apiCall<{ detail: string }>(
        "delete",
        `/api/v1/notifications/settings/groups/${groupId}/members/${userId}`,
      ),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: [...KEY, "group", vars.groupId] }),
  });
}
